import { lazy } from 'react';
import type { MessageKey } from '../../shared/i18n';

const MainPage = lazy(() =>
  import('../../pages/MainPage').then((module) => ({
    default: module.MainPage,
  }))
);

const HomeConceptFourPage = lazy(() =>
  import('../../pages/HomeConceptPage').then((module) => ({
    default: module.HomeConceptFourPage,
  }))
);

const DevPage = lazy(() =>
  import('../../pages/DevPage').then((module) => ({
    default: module.DevPage,
  }))
);

const AuthPage = lazy(() =>
  import('../../pages/AuthPage').then((module) => ({
    default: module.AuthPage,
  }))
);

const ChainPage = lazy(() =>
  import('../../pages/ChainPage').then((module) => ({
    default: module.ChainPage,
  }))
);

const GoalsPage = lazy(() =>
  import('../../pages/GoalsPage').then((module) => ({
    default: module.GoalsPage,
  }))
);

const InitiativesPage = lazy(() =>
  import('../../pages/InitiativesPage').then((module) => ({
    default: module.InitiativesPage,
  }))
);

const CreateGoalPage = lazy(() =>
  import('../../pages/GoalsPage').then((module) => ({
    default: module.CreateGoalPage,
  }))
);

const GoalPage = lazy(() =>
  import('../../pages/GoalPage').then((module) => ({
    default: module.GoalPage,
  }))
);

const CommunityPage = lazy(() =>
  import('../../pages/CommunityPage').then((module) => ({
    default: module.CommunityPage,
  }))
);

const AboutPage = lazy(() =>
  import('../../pages/AboutPage').then((module) => ({
    default: module.AboutPage,
  }))
);

const FaqPage = lazy(() =>
  import('../../pages/FaqPage').then((module) => ({
    default: module.FaqPage,
  }))
);

const PrivacyPage = lazy(() =>
  import('../../pages/PrivacyPage').then((module) => ({
    default: module.PrivacyPage,
  }))
);

const TermsPage = lazy(() =>
  import('../../pages/TermsPage').then((module) => ({
    default: module.TermsPage,
  }))
);

const SupportPage = lazy(() =>
  import('../../pages/SupportPage').then((module) => ({
    default: module.SupportPage,
  }))
);

const BlogPage = lazy(() =>
  import('../../pages/BlogPage').then((module) => ({
    default: module.BlogPage,
  }))
);

const CareersPage = lazy(() =>
  import('../../pages/CareersPage').then((module) => ({
    default: module.CareersPage,
  }))
);

const DonatePage = lazy(() =>
  import('../../pages/DonatePage').then((module) => ({
    default: module.DonatePage,
  }))
);

const DonatePaymentPage = lazy(() =>
  import('../../pages/DonatePage').then((module) => ({
    default: module.DonatePaymentPage,
  }))
);

const TeamPage = lazy(() =>
  import('../../pages/TeamPage').then((module) => ({
    default: module.TeamPage,
  }))
);

const PlatformPage = lazy(() =>
  import('../../pages/PlatformPage').then((module) => ({
    default: module.PlatformPage,
  }))
);

export const NotFoundPage = lazy(() =>
  import('../../pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  }))
);

const ProfilePage = lazy(() =>
  import('../../pages/ProfilePage').then((module) => ({
    default: module.ProfilePage,
  }))
);

const KycConfirmPage = lazy(() =>
  import('../../pages/KycConfirmPage').then((module) => ({
    default: module.KycConfirmPage,
  }))
);

const KycPage = lazy(() =>
  import('../../pages/KycPage').then((module) => ({
    default: module.KycPage,
  }))
);

type NavPlacement = 'topBar' | 'footer';

export interface AppRouteDefinition {
  path: string;
  component: typeof MainPage;
  labelKey?: MessageKey;
  navPlacements?: NavPlacement[];
  navMatchEnd?: boolean;
  requiresAuth?: boolean;
}

export const appRouteDefinitions: AppRouteDefinition[] = [
  {
    path: '/',
    component: MainPage,
    labelKey: 'app.navigation.main',
    navPlacements: ['topBar', 'footer'],
    navMatchEnd: true,
  },
  {
    path: '/home/4',
    component: HomeConceptFourPage,
  },
  {
    path: '/chain',
    component: ChainPage,
    labelKey: 'app.navigation.chain',
    navPlacements: ['topBar', 'footer'],
    requiresAuth: true,
  },
  {
    path: '/initiatives',
    component: InitiativesPage,
    labelKey: 'app.navigation.initiatives',
    navPlacements: ['topBar'],
  },
  {
    path: '/goals',
    component: GoalsPage,
  },
  {
    path: '/goals/new',
    component: CreateGoalPage,
    requiresAuth: true,
  },
  {
    path: '/goals/:id',
    component: GoalPage,
  },
  {
    path: '/community',
    component: CommunityPage,
  },
  {
    path: '/about',
    component: AboutPage,
    labelKey: 'footer.about',
    navPlacements: ['footer'],
  },
  {
    path: '/faq',
    component: FaqPage,
    labelKey: 'footer.faq',
    navPlacements: ['footer'],
  },
  {
    path: '/privacy',
    component: PrivacyPage,
    labelKey: 'footer.privacy',
    navPlacements: ['footer'],
  },
  {
    path: '/terms',
    component: TermsPage,
    labelKey: 'footer.terms',
    navPlacements: ['footer'],
  },
  {
    path: '/support',
    component: SupportPage,
    labelKey: 'footer.support',
    navPlacements: ['footer'],
  },
  {
    path: '/blog',
    component: BlogPage,
    labelKey: 'footer.blog',
    navPlacements: ['footer'],
  },
  {
    path: '/careers',
    component: CareersPage,
    labelKey: 'footer.careers',
    navPlacements: ['footer'],
  },
  {
    path: '/donate',
    component: DonatePage,
    labelKey: 'footer.donate',
    navPlacements: ['footer'],
  },
  {
    path: '/donate/pay',
    component: DonatePaymentPage,
  },
  {
    path: '/team',
    component: TeamPage,
  },
  {
    path: '/platform',
    component: PlatformPage,
    labelKey: 'footer.platform',
    navPlacements: ['footer'],
  },
  {
    path: '/auth',
    component: AuthPage,
  },
  {
    path: '/dev',
    component: DevPage,
  },
  {
    path: '/profile',
    component: ProfilePage,
  },
  {
    path: '/kyc',
    component: KycPage,
  },
  {
    path: '/kyc/confirm',
    component: KycConfirmPage,
  },
];

export function getNavigationRoutes(
  placement: NavPlacement,
  isAuthenticated = true
) {
  return appRouteDefinitions.filter(
    (route) =>
      route.navPlacements?.includes(placement) &&
      (isAuthenticated || !route.requiresAuth)
  );
}
